/**
 * AppContext
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  ReactNode,
} from "react";
import { SESSION_KEYS_TO_CLEAR } from "@/lib/constants";
import { AlertTriangle } from "lucide-react";
import { supabase, clearSessionPersistenceFlags } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import * as dm from "@/lib/dateManager";
import { useReminderScheduler } from "@/hooks/useReminderScheduler";
import { setRollbarUser } from "@/lib/rollbar";
import { recordUserSession } from "@/lib/recordUserSession";
import { useQueryClient } from "@tanstack/react-query";
import { trackedDaysPrefix, trackedDaysKey } from "@/hooks/queries/useTrackedDays";
import { dayTypesPrefix } from "@/hooks/queries/useDayTypes";
import { circlesSchedulePrefix } from "@/features/circles/queryKeys";

import type {
  ViewMode,
  DayType,
  MarkedDay,
  UserProfile,
  Preferences,
  NotificationSettings,
  AppState,
  WorkLocation,
  LocationVisibility,
  WorkTimeBlock,
  WorkDaySchedule,
  WeekDay,
  WorkSchedule,
} from "@/features/app-context/types";
import {
  emptyProfile,
  defaultPreferences,
  defaultNotifications,
  DEFAULT_DAY_TYPES,
} from "@/features/app-context/defaults";
import {
  getErrorMessage,
} from "@/features/app-context/utils";
import { parseIcsDates } from "@/lib/icsParser";
import { parseIcsDatesInWorker } from "@/lib/icsWorker";
import { warnIfStorageNearlyFull } from "@/lib/storageQuota";
import {
  getEffectiveIncludeWeekends,
  getEffectivePreferences,
  migrateWorkSchedule,
} from "@/features/app-context/workScheduleUtils";
import { useGoalPeriodStats } from "@/features/calendar/hooks/useGoalPeriodStats";
import { useLiveToday } from "@/features/calendar/hooks/useLiveToday";
import {
  clearOnboardingDefer,
  deferOnboarding as persistOnboardingDefer,
  isOnboardingDeferred,
} from "@/lib/onboardingDefer";
import {
  parseSuggestionMemory,
  readLegacyLocalSuggestionMemory,
  backfillLegacySuggestionMemoryToDb,
  clearLocalSuggestionMemory,
} from "@/features/rhythm-intelligence/suggestionMemory";
import {
  mergeOAuthProfileFields,
  resolveOAuthProfileBasicsFromUser,
} from "@/lib/profileAvatar";
import { syncOAuthProfileFromAuthUser } from "@/lib/oauthProfileSync";

// Re-exports preserve the public surface of this module so existing
// callers (`import { DayType } from "@/contexts/AppContext"`, etc.)
// keep working untouched.
export type {
  ViewMode,
  DayType,
  MarkedDay,
  UserProfile,
  Preferences,
  NotificationSettings,
  WorkLocation,
  LocationVisibility,
  WorkTimeBlock,
  WorkDaySchedule,
  WeekDay,
  WorkSchedule,
};
export {
  defaultPreferences,
  defaultNotifications,
  DEFAULT_DAY_TYPES,
};

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

async function getAuthenticatedUserId(): Promise<string> {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) throw new Error(`Session error: ${error.message}`);
  if (!session?.user) throw new Error("No active session. Please log in again.");
  return session.user.id;
}

// ─────────────────────────────────────────────
// IMPORTED-HOLIDAY PERSISTENCE (server-visible mirror)
//
// The ICS feed is parsed client-side, but the notification engine (Edge
// Functions) needs to know a user's imported holidays to (a) skip the daily
// reminder and (b) enrich the digest. We mirror the parsed feed into
// `public.user_imported_holidays`, reconciling on every refresh and clearing
// on disconnect. Best-effort: a failure here must never break the calendar.
// ─────────────────────────────────────────────
async function syncImportedHolidaysToDb(
  holidays: Record<string, string>,
  sourceUrl: string,
): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    const userId = user?.id;
    if (!userId) return;

    const dates = Object.keys(holidays);
    if (dates.length === 0) {
      await supabase.from("user_imported_holidays").delete().eq("user_id", userId);
      return;
    }

    const rows = dates.map((date) => ({
      user_id:      userId,
      holiday_date: date,
      name:         holidays[date] || "Holiday",
      source_url:   sourceUrl || null,
      updated_at:   new Date().toISOString(),
    }));

    const { error: upsertErr } = await supabase
      .from("user_imported_holidays")
      .upsert(rows, { onConflict: "user_id,holiday_date" });
    if (upsertErr) throw upsertErr;

    // Prune any rows the feed no longer contains.
    const inList = `(${dates.map((d) => `"${d}"`).join(",")})`;
    const { error: pruneErr } = await supabase
      .from("user_imported_holidays")
      .delete()
      .eq("user_id", userId)
      .not("holiday_date", "in", inList);
    if (pruneErr) throw pruneErr;
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn("[AppContext] imported-holiday sync failed:", getErrorMessage(err));
    }
  }
}

async function clearImportedHolidaysInDb(): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    const userId = user?.id;
    if (!userId) return;
    await supabase.from("user_imported_holidays").delete().eq("user_id", userId);
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn("[AppContext] imported-holiday clear failed:", getErrorMessage(err));
    }
  }
}

// ─────────────────────────────────────────────
// CONTEXT
// ─────────────────────────────────────────────

const AppContext = createContext<AppState>({} as AppState);

export function AppProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [isAuthenticated, setAuthenticated] = useState(false);
  const [profile, setProfile]               = useState<UserProfile>(emptyProfile);
  const [preferences, setPreferences]       = useState<Preferences>(defaultPreferences);
  const [notifications, setNotifications]   = useState<NotificationSettings>(defaultNotifications);
  const [dayTypes, setDayTypes]             = useState<DayType[]>([]);
  const [markedDays, setMarkedDays]         = useState<MarkedDay[]>([]);
  const [viewMode, setViewMode]             = useState<ViewMode>("month");
  const lastSyncedGoalPeriodRef               = useRef<ViewMode | null>(null);
  const [isLoadingData, setIsLoadingData]   = useState(true);
  // `hasBootstrapped` flips to true the first time `fetchUserData` completes
  // (success OR error). Subsequent refreshes (e.g. `refreshUserData()` after
  // onboarding, a dirty-guard discard, or a Realtime profile change) will
  // still flip `isLoadingData`, but the UI no longer replaces the whole
  // app tree with a `LoadingScreen` — that was the primary cause of the
  // "refetch hell" full-screen blank flashes the audit flagged.
  const [hasBootstrapped, setHasBootstrapped] = useState(false);
  const [loadError, setLoadError]           = useState<string | null>(null);

  // ICS live subscription — in-memory only, never persisted
  const [subscribedHolidays, setSubscribedHolidays] = useState<Record<string, string>>({});

  // ── Daily reminder scheduler (fires a browser notification at the chosen time) ──
  // Live, not frozen: `useMemo(dm.getToday, [])` would pin "today" to mount
  // time, so a tab left open across midnight (or a period rollover) kept
  // suppressing the reminder based on yesterday's date. See useLiveToday.
  const reminderTodayStr = useLiveToday();
  const effectivePreferences = useMemo(
    () => getEffectivePreferences(preferences),
    [preferences],
  );
  const reminderHolidaySet = useMemo(
    () => new Set(Object.keys(subscribedHolidays ?? {})),
    [subscribedHolidays],
  );
  const reminderDayTypeMap = useMemo(
    () => new Map(dayTypes.map((t) => [t.id, t])),
    [dayTypes],
  );
  // Anchored on the live "today" string rather than a fresh `new Date()` per
  // render: same period, but a stable identity so the range memo below only
  // recomputes when the day actually changes.
  const reminderAnchorDate = useMemo(
    () => dm.parseLocalDate(reminderTodayStr),
    [reminderTodayStr],
  );
  const { goalPeriodStats: reminderGoalStats } = useGoalPeriodStats({
    currentDate: reminderAnchorDate,
    preferences: effectivePreferences,
    markedDays,
    holidaySet: reminderHolidaySet,
    dayTypeMap: reminderDayTypeMap,
  });
  const skipReminderToday = useMemo(() => {
    if (subscribedHolidays?.[reminderTodayStr]) return true;
    if (markedDays.some((d) => d.date === reminderTodayStr)) return true;
    if (reminderGoalStats.isGoalMet) return true;
    return false;
  }, [
    subscribedHolidays,
    markedDays,
    reminderTodayStr,
    reminderGoalStats.isGoalMet,
  ]);

  useReminderScheduler({
    reminderTime:    notifications.reminderTime,
    includeWeekends: getEffectiveIncludeWeekends(
      preferences.workSchedule,
      preferences.includeWeekends,
    ),
    pushEnabled:     notifications.pushReminders,
    skipToday:       skipReminderToday,
  });

  const [onboardingDeferred, setOnboardingDeferred] = useState(isOnboardingDeferred);

  const showOnboardingValue =
    isAuthenticated && !profile.isOnboarded && !onboardingDeferred;
  const showOnboardingReminderValue =
    isAuthenticated && !profile.isOnboarded && onboardingDeferred;

  const deferOnboarding = useCallback(() => {
    persistOnboardingDefer();
    setOnboardingDeferred(true);
  }, []);

  const resumeOnboarding = useCallback(() => {
    clearOnboardingDefer();
    setOnboardingDeferred(false);
  }, []);

  useEffect(() => {
    if (profile.isOnboarded) {
      clearOnboardingDefer();
      setOnboardingDeferred(false);
    }
  }, [profile.isOnboarded]);

  // Default calendar view follows the office-goal period from Settings.
  // Syncs on first preferences load and whenever goalPeriod changes — not on
  // every refresh, so a manual view pick persists until the user changes their goal.
  useEffect(() => {
    const gp = preferences.goalPeriod ?? "month";
    if (lastSyncedGoalPeriodRef.current === gp) return;
    setViewMode(gp);
    lastSyncedGoalPeriodRef.current = gp;
  }, [preferences.goalPeriod]);

  const isMounted     = useRef(true);
  const loadingForUid = useRef<string | null>(null);

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  const setShowOnboarding = useCallback((_v: boolean) => {
    // No-op: onboarding is now driven by profile.is_onboarded in the DB
  }, []);

  /**
   * Public refresh method to trigger a full re-fetch of user data.
   * Useful after mutations (like onboarding completion) to sync global state.
   */
  const refreshUserData = useCallback(async (): Promise<void> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user?.id) {
      await fetchUserData(session.user.id);
    }
  }, []);

  // ─────────────────────────────────────────────
  // ICS LIVE SUBSCRIPTION
  //
  // Calls the `fetch-ics` Edge Function (CORS proxy), parses the ICS text
  // and saves the dates in subscribedHolidays.
  //
  // Deliberate design:
  // - Does not block app load (fire-and-forget from fetchUserData)
  // - Fails silently: holidays are nice-to-have, never critical
  // - Exposed in AppState so Settings.tsx can call it after saving the URL
  // ─────────────────────────────────────────────
  //
  // Sprint 3 — Bug 13 fix: the previous implementation swallowed every
  // error via `catch (_err) {}`, so a misconfigured Edge Function (e.g.
  // `fetch-ics` not deployed) failed silently and the calendar showed
  // no holidays while Settings still claimed "connected". The function
  // now returns `{ holidays } | { error }` so the Settings Sync handler
  // can surface a real toast AND the holidays map still updates
  // correctly on success. Background refreshes from `fetchUserData`
  // can keep passing `{ silent: true }` to preserve the non-blocking
  // boot contract.
  //
  const refreshIcsSubscription = useCallback(
    async (
      url: string,
      opts: { silent?: boolean } = {},
    ): Promise<{ holidays: Record<string, string> } | { error: string }> => {
      if (!url.trim()) {
        if (isMounted.current) setSubscribedHolidays({});
        // Disconnect: wipe the server-visible mirror so reminders/digest stop
        // treating stale dates as holidays. Fire-and-forget.
        void clearImportedHolidaysInDb();
        return { holidays: {} };
      }

      try {
        const { data, error } = await supabase.functions.invoke("fetch-ics", {
          body: { icsUrl: url.trim() },
        });

        // Network / gateway error from the Functions transport.
        if (error) throw new Error(error.message);
        // Soft error returned as 200 `{ error }` by the Edge Function.
        if (data?.error) throw new Error(data.error);

        const icsText = data?.icsText ?? "";
        const holidays =
          icsText.length > 50_000
            ? await parseIcsDatesInWorker(icsText)
            : parseIcsDates(icsText);

        void warnIfStorageNearlyFull();

        if (isMounted.current) {
          setSubscribedHolidays(holidays);
        }
        // Mirror the parsed feed into the DB so the server can skip reminders
        // on these days and surface them in the digest. Fire-and-forget.
        void syncImportedHolidaysToDb(holidays, url.trim());
        return { holidays };
      } catch (err) {
        const message = getErrorMessage(err);
        if (!opts.silent && import.meta.env.DEV) {
          console.warn("[AppContext] ICS refresh failed:", message);
        }
        return { error: message };
      }
    },
    [],
  );

  // ─────────────────────────────────────────────
  // INITIALIZE NEW USER
  // ─────────────────────────────────────────────
  const initializeUser = useCallback(async (userId: string): Promise<void> => {
    const { error: settingsError } = await supabase
      .from("user_settings")
      .insert({
        user_id:                userId,
        goal_period:            defaultPreferences.goalPeriod,
        goal_type:              defaultPreferences.goalType,
        goal_value:             defaultPreferences.goalValue,
        confetti_enabled:       defaultPreferences.confettiEnabled,
        include_weekends:       defaultPreferences.includeWeekends,
        year_type:              defaultPreferences.yearType,
        fiscal_start_month:     defaultPreferences.fiscalStartMonth,
        ai_suggestions_enabled: false,
        email_reminders:        true,
        push_reminders:         true,
        reminder_frequency:     "daily",
        reminder_time:          defaultNotifications.reminderTime,
        ics_url:                null,
      });

    if (settingsError) throw new Error(`Failed to create settings: ${settingsError.message}`);

    if (isMounted.current) {
      setPreferences(defaultPreferences);
      setNotifications(defaultNotifications);
      setMarkedDays([]);
    }
  }, []);

  // ─────────────────────────────────────────────
  // FETCH ALL USER DATA
  // ─────────────────────────────────────────────
  const fetchUserData = useCallback(async (userId: string): Promise<void> => {
    if (loadingForUid.current === userId) return;
    loadingForUid.current = userId;

    if (isMounted.current) {
      setIsLoadingData(true);
      setLoadError(null);
    }

    try {
      const [profileRes, settingsRes, sysDayTypesRes, overridesRes, trackedRes] = await Promise.all([
        supabase.from("profiles").select("*").eq("id", userId).single(),
        supabase.from("user_settings").select("*").eq("user_id", userId).maybeSingle(),
        supabase.from("system_day_types").select("*"),
        supabase.from("user_day_type_overrides").select("*").eq("user_id", userId),
        supabase.from("tracked_days").select("date, system_day_type_id, status").eq("user_id", userId),
      ]);

      if (!isMounted.current || loadingForUid.current !== userId) return;

      // ── Profile ────────────────────────────────────────────────────────────
      // FIX: Profile fetch failure is now fatal.
      // A missing profile means downstream queries (including the Circle
      // avatar system) cannot function correctly. Surface the error rather
      // than silently continuing with an empty profile, which would leave
      // profile.email = "" and silently disable the Circle calendar query.
      if (profileRes.error) {
        throw new Error(`Profile fetch failed: ${profileRes.error.message}`);
      }
      if (!profileRes.data) {
        throw new Error(
          "Profile not found. Your account may still be initializing — please refresh in a few seconds."
        );
      }

      const p = profileRes.data;

      // Google/OAuth signups expose display basics in Auth metadata. When
      // the local profile is still blank, copy those values into the
      // canonical profile row so onboarding starts filled in and Circle
      // peers see a real photo. These fields are display-only, never authz.
      let resolvedFirstName = p.first_name || "";
      let resolvedLastName = p.last_name || "";
      let resolvedAvatarUrl = p.avatar_url || "";
      const resolvedBirthday = (p.birthday as string | null) ?? null;
      const resolvedBirthdayConfirmed = Boolean(p.birthday_confirmed && resolvedBirthday);
      const resolvedBirthdaySource = (p.birthday_source as "manual" | "google" | null) ?? null;
      if (!resolvedAvatarUrl || !resolvedFirstName || !resolvedLastName) {
        const { data: userRes } = await supabase.auth.getUser();
        const authUser = userRes.user?.id === userId ? userRes.user : null;
        const oauthProfile = resolveOAuthProfileBasicsFromUser(authUser);

        if (authUser) {
          await syncOAuthProfileFromAuthUser(authUser, p);
        }

        const merged = mergeOAuthProfileFields(
          {
            firstName: resolvedFirstName,
            lastName: resolvedLastName,
            avatarUrl: resolvedAvatarUrl,
          },
          oauthProfile,
        );
        resolvedFirstName = merged.firstName;
        resolvedLastName = merged.lastName;
        resolvedAvatarUrl = merged.avatarUrl;
      }

      // ── Timezone auto-sync ─────────────────────────────────────────────────
      //
      // If the stored `profiles.timezone` is the default `'UTC'` (i.e. never
      // explicitly set) and the browser is reporting something else, we
      // adopt the browser zone as the user's canonical timezone.
      //
      // Guard rails:
      //   • Only fires when the DB value is still the default — never
      //     overwrites an explicit user choice made from Settings.
      //   • Only fires when the browser zone is a real IANA string — the
      //     DB has a `CHECK (is_valid_timezone(timezone))` constraint, so
      //     sending garbage would raise a 400 and we'd crash profile load.
      //   • Best-effort: a failure here is logged but non-fatal so users
      //     without `Intl` support or with restrictive CSP still boot.
      let resolvedTimezone = p.timezone || "UTC";
      try {
        const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const dbTz      = p.timezone || "UTC";
        if (browserTz && browserTz !== "UTC" && dbTz === "UTC") {
          const { error: tzErr } = await supabase
            .from("profiles")
            .update({ timezone: browserTz })
            .eq("id", userId);
          if (tzErr) {
            console.warn("[AppContext] timezone sync failed:", tzErr.message);
          } else {
            resolvedTimezone = browserTz;
          }
        }
      } catch (err) {
        console.warn("[AppContext] timezone detection skipped:", err);
      }

      setProfile({
        userId:    p.id,
        firstName: resolvedFirstName,
        lastName:  resolvedLastName,
        email:     p.email || "",
        avatarUrl: resolvedAvatarUrl,
        birthday:  resolvedBirthday,
        birthdayConfirmed: resolvedBirthdayConfirmed,
        birthdaySource: resolvedBirthdaySource,
        timezone:  resolvedTimezone,
        isOnboarded: !!p.is_onboarded,
      });

      // ── Settings ───────────────────────────────────────────────────────────
      // select("*") guarantees every column is returned, so all fields below
      // are populated from the DB and never fall back to stale in-memory defaults.
      if (settingsRes.error) {
        throw new Error(`Settings fetch failed: ${settingsRes.error.message}`);
      }

      if (!settingsRes.data) {
        await initializeUser(userId);
        loadingForUid.current = null;
        await fetchUserData(userId);
        return;
      }

      const s = settingsRes.data as Record<string, unknown>;
      const workSchedule = migrateWorkSchedule(s.work_schedule);
      const dbMemory = parseSuggestionMemory(s.suggestion_memory);
      const legacyMemory = !s.suggestion_memory
        ? readLegacyLocalSuggestionMemory(userId)
        : null;
      const suggestionMemory = legacyMemory ?? dbMemory;
      const hasMemoryHistory =
        suggestionMemory.totalAccepts > 0 || suggestionMemory.totalRejects > 0;

      if (legacyMemory) {
        void backfillLegacySuggestionMemoryToDb(userId, legacyMemory);
      }

      setPreferences({
        goalPeriod:           (s.goal_period            as "week" | "month" | "quarter") ?? "month",
        goalType:             (s.goal_type              as "percent" | "days")           ?? "percent",
        goalValue:            (s.goal_value             as number)                       ?? 50,
        confettiEnabled:      (s.confetti_enabled       as boolean)                      ?? true,
        includeWeekends:      (s.include_weekends       as boolean)                      ?? false,
        yearType:             (s.year_type              as "natural" | "fiscal")         ?? "natural",
        fiscalStartMonth:     (s.fiscal_start_month     as number)                       ?? 1,
        aiSuggestionsEnabled: (s.ai_suggestions_enabled as boolean)                      ?? false,
        icsUrl:               (s.ics_url                as string)                       ?? "",
        circleShareScope:     (s.circle_share_scope     as Preferences["circleShareScope"]) ?? "month",
        circleHiddenPeerIds:  Array.isArray(s.circle_hidden_peer_ids)
                                ? (s.circle_hidden_peer_ids as string[])
                                : [],
        ...(workSchedule ? { workSchedule } : {}),
        ...(hasMemoryHistory ? { suggestionMemory } : {}),
      });
      // Hydrate the three-pillar notification config from `profiles`
      // (new canonical source) while also preserving the legacy fields
      // sourced from `user_settings` for backwards compatibility.
      const notifRaw = ((p.notif_settings ?? {}) as Record<string, unknown>);
      const rawReminderTime = (p.reminder_time as string | null) ?? null;
      const normalizedReminderTime = rawReminderTime
        ? rawReminderTime.slice(0, 5)              // "HH:MM:SS" → "HH:MM"
        : ((s.reminder_time as string) ?? "09:00");

      setNotifications({
        // Legacy
        emailReminders: (s.email_reminders    as boolean)                          ?? true,
        pushReminders:  (s.push_reminders     as boolean)                          ?? true,
        frequency:      (s.reminder_frequency as "daily" | "weekly" | "monthly")  ?? "daily",
        reminderTime:   normalizedReminderTime,

        // Pillar 1 — Smart Reminders
        reminderEnabled:      (p.reminder_enabled       as boolean) ?? false,
        reminderChannelEmail: (notifRaw.reminder_email  as boolean) ?? true,
        reminderChannelInApp: (notifRaw.reminder_inapp  as boolean) ?? true,

        // Pillar 2 — Transactional (defaults match DB jsonb defaults)
        circleInviteEmail: (notifRaw.circle_invite_email as boolean) ?? true,
        circleInviteInApp: (notifRaw.circle_invite_inapp as boolean) ?? true,
        eventTaggedEmail:  (notifRaw.event_tagged_email  as boolean) ?? true,
        eventTaggedInApp:  (notifRaw.event_tagged_inapp  as boolean) ?? true,

        // Pillar 3 — Strategic Digest
        digestEnabled:   (p.digest_enabled    as boolean)               ?? defaultNotifications.digestEnabled,
        digestFrequency: (p.digest_frequency  as "weekly" | "monthly") ?? "weekly",
      });

      // ── ICS live subscription ──────────────────────────────────────────────
      // Fire-and-forget: we don't block the app load.
      // If the user has a saved icsUrl, we load it in the background.
      const icsUrl = (s.ics_url as string) ?? "";
      if (icsUrl) {
        // `silent: true` so boot-time refresh failures don't spam the
        // dev console. The Settings page's manual Sync button uses the
        // non-silent path and surfaces any errors to the user.
        refreshIcsSubscription(icsUrl, { silent: true }).catch(() => {
          // Failure is non-blocking at boot time.
        });
      }

      // ── Day types ──────────────────────────────────────────────────────────
      if (sysDayTypesRes.error) {
        // Warning skipped in production
      } else if (sysDayTypesRes.data) {
        const overrides = new Map((overridesRes.data as { system_day_type_id: string; custom_name: string | null; custom_color: string | null }[])?.map((o) => [o.system_day_type_id, o]) || []);
        setDayTypes(
          sysDayTypesRes.data.map((st) => {
            const ov = overrides.get(st.id);
            return {
              id:           st.id,
              name:         ov?.custom_name || st.system_name,
              color:        ov?.custom_color || st.system_color,
              visible:      st.is_user_selectable,
              baseCategory: st.base_category,
            };
          })
        );
      }

      // ── Tracked days ───────────────────────────────────────────────────────
      if (trackedRes.error) {
        // Warning skipped in production
      } else if (trackedRes.data) {
        setMarkedDays(
          trackedRes.data.map((td) => ({
            date:   td.date,
            typeId: td.system_day_type_id,
            status: td.status as "confirmed" | "unconfirmed",
          }))
        );
      }

    } catch (err) {
      const msg = getErrorMessage(err);
      if (isMounted.current) {
        setLoadError(msg);
        toast({
          title:       "Failed to load your data",
          description: "Please refresh the page. If the problem persists, contact support.",
          variant:     "destructive",
        });
      }
    } finally {
      if (isMounted.current) {
        setIsLoadingData(false);
        setHasBootstrapped(true);
      }
      loadingForUid.current = null;
    }
  }, [initializeUser, refreshIcsSubscription]);

  // ─────────────────────────────────────────────
  // REFRESH CALENDAR DATA
  // ─────────────────────────────────────────────
  const refreshCalendarData = useCallback(async (): Promise<void> => {
    try {
      const userId = await getAuthenticatedUserId();
      const { data, error } = await supabase
        .from("tracked_days")
        .select("date, system_day_type_id, status")
        .eq("user_id", userId);
      if (error) throw error;
      if (isMounted.current && data) {
        setMarkedDays(data.map((td) => ({ date: td.date, typeId: td.system_day_type_id, status: td.status as "confirmed" | "unconfirmed" })));
      }
    } catch (err) {
      toast({
        title: "Refresh failed",
        description: getErrorMessage(err),
        variant: "destructive",
      });
    }
  }, []);

  // ─────────────────────────────────────────────
  // AUTH LISTENER
  // ─────────────────────────────────────────────
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!isMounted.current) return;
      if (session?.user) {
        setRollbarUser({ id: session.user.id });
        recordUserSession();
        setAuthenticated(true);
        fetchUserData(session.user.id);
      } else {
        setAuthenticated(false);
        setIsLoadingData(false);
        setHasBootstrapped(true);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted.current) return;

      if (event === "SIGNED_IN" && session?.user) {
        setRollbarUser({ id: session.user.id });
        recordUserSession();
        setAuthenticated(true);
        fetchUserData(session.user.id);
      } else if (event === "TOKEN_REFRESHED") {
        // Token silently refreshed — no need to re-fetch data
      } else if (event === "SIGNED_OUT") {
        setAuthenticated(false);
        setProfile(emptyProfile);
        setMarkedDays([]);
        setDayTypes([]);
        setPreferences(defaultPreferences);
        setNotifications(defaultNotifications);
        setLoadError(null);
        setIsLoadingData(false);
        // Clear ICS subscription on sign-out
        setSubscribedHolidays({});
        loadingForUid.current = null;
        setRollbarUser(null);
      }
    });

    return () => { subscription.unsubscribe(); };
  }, [fetchUserData]);

  // ─────────────────────────────────────────────
  // MARK DAYS
  //
  // Finder strategy — three-tier fallback to survive any DB migration state:
  //   1. baseCategory  — authoritative post-migration value (preferred)
  //   2. name          — legacy pre-migration rows that still carry the correct name
  //   3. color         — last-resort for rows whose name was already customised by
  //                      the user but base_category column not yet backfilled
  // ─────────────────────────────────────────────
  const markDays = useCallback(async ({ dates, typeId, status }: { dates: string[]; typeId: string; status?: "confirmed" | "unconfirmed" }): Promise<void> => {
    let userId: string;
    try { userId = await getAuthenticatedUserId(); }
    catch (_err) {
      toast({ title: "Session expired", description: "Please log in again.", variant: "destructive" });
      return;
    }

    const today = dm.getToday();

    const inserts = dates.map((date) => {
      // Find the specific day type row being applied
      const selectedType = dayTypes.find((t) => t.id === typeId);
      
      // Determine if this is an "office-equivalent" work day (Office, Travel, Visit)
      const isOfficeEquivalent = selectedType?.baseCategory === "WORK_OFFICE" || 
                                selectedType?.baseCategory === "WORK_EXTERNAL" ||
                                typeId === "office" || typeId === "client_visit" || typeId === "business_travel";

      // If status is passed explicitly (e.g. from confirmation prompt), use it.
      // Otherwise, default to "unconfirmed" for future office-equivalent days.
      let finalStatus: "confirmed" | "unconfirmed" = status ?? "confirmed";
      if (!status) {
        finalStatus = (isOfficeEquivalent && date > today) ? "unconfirmed" : "confirmed";
      }

      return {
        user_id:     userId,
        date,
        system_day_type_id: typeId,
        status:      finalStatus,
      };
    });

    try {
      const { error } = await supabase
        .from("tracked_days")
        .upsert(inserts, { onConflict: "user_id,date" });
      if (error) throw error;

      if (isMounted.current) {
        setMarkedDays((prev) => {
          const filtered = prev.filter((d) => !dates.includes(d.date));
          return [...filtered, ...inserts.map((i) => ({ date: i.date, typeId: i.system_day_type_id, status: i.status as "confirmed" | "unconfirmed" }))];
        });
        // Fan out cache invalidation so React-Query-driven views
        // (Dashboard calendar grid, Analytics, Circles schedule)
        // refetch — otherwise they keep rendering the stale ghost
        // mark/clear and the user thinks the write silently failed.
        queryClient.invalidateQueries({ queryKey: trackedDaysPrefix });
        queryClient.invalidateQueries({ queryKey: circlesSchedulePrefix });
      }
    } catch (err) {
      toast({ title: "Error saving", description: getErrorMessage(err), variant: "destructive" });
    }
  }, [dayTypes, queryClient]);

  // ─────────────────────────────────────────────
  // CLEAR DAYS
  // ─────────────────────────────────────────────
  const clearDays = useCallback(async (dates: string[]): Promise<void> => {
    let userId: string;
    try { userId = await getAuthenticatedUserId(); }
    catch (_err) {
      toast({ title: "Session expired", description: "Please log in again.", variant: "destructive" });
      return;
    }

    try {
      const { error } = await supabase
        .from("tracked_days")
        .delete()
        .eq("user_id", userId)
        .in("date", dates);
      if (error) throw error;

      if (isMounted.current) {
        setMarkedDays((prev) => prev.filter((d) => !dates.includes(d.date)));
        // Same rationale as `markDays`: the delete succeeded server-
        // side but any consumer binding to `useTrackedDays(userId)`
        // will keep painting the deleted day until its cache is
        // invalidated. Fan out here so the Notification Bell's "No,
        // clear" truly removes the day everywhere in the UI.
        queryClient.invalidateQueries({ queryKey: trackedDaysPrefix });
        queryClient.invalidateQueries({ queryKey: circlesSchedulePrefix });
      }
    } catch (err) {
      toast({ title: "Error clearing days", description: getErrorMessage(err), variant: "destructive" });
    }
  }, [queryClient]);

  // ─────────────────────────────────────────────
  // CLEAR CURRENT MONTH
  // ─────────────────────────────────────────────
  const clearCurrentMonth = useCallback(async (): Promise<void> => {
    let userId: string;
    try { userId = await getAuthenticatedUserId(); }
    catch (err) { throw new Error(getErrorMessage(err)); }

    // Derive the current month's bounds entirely through dateManager — no raw Date
    // construction, no manual zero-padding, no UTC-shift risk.
    const todayStr = dm.getToday();
    const [y, rawM] = todayStr.split("-").map(Number);
    const m0 = rawM - 1; // 0-based month for dm.getMonthDays / dm.buildDateStr
    const { daysInMonth } = dm.getMonthDays(y, m0);
    const startDate = dm.buildDateStr(y, m0, 1);
    const endDate   = dm.buildDateStr(y, m0, daysInMonth);
    // The prefix is the YYYY-MM portion — safe to derive from the canonical today string.
    const prefix = todayStr.slice(0, 7); // "YYYY-MM"

    const { error } = await supabase
      .from("tracked_days")
      .delete()
      .eq("user_id", userId)
      .gte("date", startDate)
      .lte("date", endDate);

    if (error) throw new Error(error.message);

    if (isMounted.current) {
      setMarkedDays((prev) => prev.filter((d) => !d.date.startsWith(prefix)));
      // Forced UI refresh: Invalidate trackedDays cache so Dashboard and other
      // consumers repaints instantly without manual reload.
      queryClient.invalidateQueries({ queryKey: trackedDaysPrefix });
    }
  }, []);

  // ─────────────────────────────────────────────
  // FACTORY RESET
  // ─────────────────────────────────────────────
  const factoryReset = useCallback(async (): Promise<void> => {
    let userId: string;
    try { userId = await getAuthenticatedUserId(); }
    catch (err) { throw new Error(getErrorMessage(err)); }

    const deletions = await Promise.all([
      supabase.from("tracked_days").delete().eq("user_id", userId),
      supabase.from("user_day_type_overrides").delete().eq("user_id", userId),
      supabase.from("user_settings").delete().eq("user_id", userId),
    ]);

    const deleteError = deletions.find((r) => r.error)?.error;
    if (deleteError) throw new Error(`Reset failed: ${deleteError.message}`);

    await clearImportedHolidaysInDb();
    clearLocalSuggestionMemory(userId);

    if (isMounted.current) {
      setSubscribedHolidays({});
      setMarkedDays([]);
    }

    // Dashboard reads tracked days from a *persisted* React Query cache
    // (see App.tsx). DB deletes alone are not enough — wipe the cache so a
    // post-reset reload cannot resurrect stale marked days.
    queryClient.setQueryData(trackedDaysKey(userId), []);
    await queryClient.removeQueries({ queryKey: trackedDaysPrefix });
    await queryClient.removeQueries({ queryKey: dayTypesPrefix });

    await initializeUser(userId);
    loadingForUid.current = null;
    await fetchUserData(userId);
  }, [fetchUserData, initializeUser, queryClient]);

  // ─────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────
  return (
    <AppContext.Provider
      value={{
        isAuthenticated,  setAuthenticated,
        profile,          setProfile,
        preferences,      setPreferences,
        notifications,    setNotifications,
        dayTypes,         setDayTypes,
        markedDays,       setMarkedDays,
        markDays,         clearDays,
        clearCurrentMonth,
        factoryReset,
        refreshCalendarData,
        showOnboarding:   showOnboardingValue,
        showOnboardingReminder: showOnboardingReminderValue,
        deferOnboarding,
        resumeOnboarding,
        setShowOnboarding,
        viewMode,         setViewMode,
        isLoadingData,
        loadError,
        subscribedHolidays,
        refreshIcsSubscription,
        refreshUserData,
      }}
    >
      {/* Gate the full-screen splash on INITIAL boot only. Once the app has
          hydrated once, every subsequent fetchUserData() call runs in the
          background — consumers see cached data, a thin top-bar progress
          indicator (rendered by AppLayout via `useIsFetching`), and the
          UI never blanks out. This is the single biggest lever against
          the "refetch hell" experience the UX audit flagged. */}
      {!hasBootstrapped && isLoadingData ? (
        <LoadingScreen />
      ) : loadError && !hasBootstrapped ? (
        <ErrorScreen message={loadError} onRetry={() => {
          supabase.auth.getSession().then(({ data: { session } }) => {
            if (session?.user) fetchUserData(session.user.id);
          });
        }} />
      ) : (
        children
      )}
    </AppContext.Provider>
  );
}

// ─────────────────────────────────────────────
// LOADING / ERROR SCREENS
// ─────────────────────────────────────────────

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-3">
      <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      <p className="text-sm text-muted-foreground font-medium">Loading Office Rhythm…</p>
    </div>
  );
}

function ErrorScreen({ message, onRetry }: { message: string; onRetry: () => void }) {
  const [isSigningOut, setIsSigningOut] = useState(false);

  const isAuthError = /invalid|expired|not found|deleted|JWT/i.test(message);

  const handleForceSignOut = async () => {
    setIsSigningOut(true);
    try {
      await supabase.auth.signOut();
      clearSessionPersistenceFlags();
      SESSION_KEYS_TO_CLEAR.forEach((k) => {
        localStorage.removeItem(k);
        sessionStorage.removeItem(k);
      });
    } catch {
      // sign out silently even on failure
    } finally {
      window.location.href = "/";
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4 p-6 text-center">
      <AlertTriangle className="h-8 w-8 text-destructive opacity-80" />
      <div>
        <p className="text-sm font-semibold text-destructive">
          {isAuthError ? "Session no longer valid" : "Failed to load your data"}
        </p>
        <p className="text-xs text-muted-foreground max-w-xs mt-2">
          {isAuthError
            ? "Your account may have been deleted or your session has expired."
            : message}
        </p>
      </div>

      <div className="flex items-center gap-6 mt-4">
        {!isAuthError && (
          <button
            onClick={onRetry}
            className="text-xs font-medium underline text-primary hover:text-primary/80 transition-colors"
          >
            Try again
          </button>
        )}
        <button
          onClick={handleForceSignOut}
          disabled={isSigningOut}
          className="text-xs font-medium underline text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSigningOut ? "Signing out…" : "Sign out & start over"}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// HOOK
// ─────────────────────────────────────────────

export const useApp = () => useContext(AppContext);
